import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocale } from '../hooks/useLocale.jsx';
import { analytics, logEvent } from '../firebase.js';
import { isLandingPath } from '../utils/landing-paths.js';

// 탭을 누른 것 자체 — 화면 조회(screen_view)와 달리 "어디로 어떻게 갔나" 를 센다.
// 이벤트 이름에 탭을 박는다: GA 커스텀 측정기준 없이 /admin 이벤트 표에서 바로 보이게.
export function logTabClick(key, surface) {
  logEvent(analytics, `nav_tab_${key}`, { surface });
}

// Mobile-only bottom navigation. Hidden on desktop via CSS @media query.
//
// 2.3.0 — 떠 있는 유리 바 5칸 (drape 2.3.0 의 glass tab bar 를 가져왔다):
//   갤러리 · 내 디자인 · [만들기] · 디자이너(Gio) · 계정
// 갤러리(스타일 랭킹)는 만들기 화면 안의 오버레이였고, Gio 는 디자인 상세
// 아래의 채팅이었다 — 둘 다 탭으로 꺼냈다. 만들기는 여전히 홈이고 가운데
// 원형 버튼이다. 피드 코드는 지운 게 아니라 라우트만 떼어놨다.
//
// Detail / modal-like pages (terms / privacy / support / landing / public share
// view) hide the tab bar entirely.
// 데스크톱 헤더(Header.jsx)도 같은 목록으로 그린다 — 두 곳의 메뉴가 갈리지 않게.
export const TABS = [
  {
    key: 'gallery',
    path: '/gallery',
    icon: 'local_fire_department',
    labelKey: 'navGallery',
    match: (p) => p.startsWith('/gallery'),
  },
  {
    key: 'my',
    path: '/my',
    // 여기 담긴 게 내가 만든 사진이라는 걸 아이콘만으로 알 수 있게.
    icon: 'photo_library',
    labelKey: 'navMy',
    match: (p) =>
      p.startsWith('/my') ||
      p.startsWith('/designs') ||
      p.startsWith('/bookmarks') ||
      p.startsWith('/c/') ||
      p.startsWith('/u/'),
  },
  {
    key: 'create',
    path: '/create',
    // 조리개(camera) 위에 작은 + 를 겹쳐 "사진으로 새로 만든다" 를 표현.
    icon: 'camera',
    labelKey: 'navCreate',
    center: true,
    // '/' 는 모바일에서 '/create' 로 보내므로 둘 다 Create 탭으로 친다.
    match: (p) => p === '/create' || p === '/',
  },
  {
    key: 'gio',
    path: '/gio',
    icon: 'forum',
    labelKey: 'navGio',
    match: (p) => p.startsWith('/gio'),
  },
  {
    key: 'account',
    path: '/account',
    // 이 탭엔 크레딧·초대·프로필·언어가 다 있어서 manage_accounts 가 맞다.
    icon: 'manage_accounts',
    labelKey: 'navAccount',
    match: (p) => p === '/account' || p === '/invite' || p === '/pricing',
  },
];
const CENTER = TABS.findIndex((tab) => tab.center);

const HIDDEN_ROUTES = ['/terms', '/privacy', '/support', '/delete-account', '/landing'];

// 탭바가 안 보이는 화면인지. Onboarding 도 이걸 쓴다 — 탭 스포트라이트는
// 탭바를 가리키는 UI 라서, 탭바가 없는 화면에서 뜨면 아무것도 없는 자리를
// 가리키며 화면만 덮는다 (스크롤까지 잠근다).
export function isTabBarHidden(pathname) {
  return HIDDEN_ROUTES.includes(pathname) || isLandingPath(pathname) || pathname.startsWith('/s/');
}

export function MobileTabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLocale();

  // 알약이 앉을 칸. 가운데 만들기는 원형 버튼이라 알약을 깔지 않는다.
  const activeSlot = TABS.findIndex((tab) => !tab.center && tab.match(pathname));

  // Liquid-glass lens (drape 와 같다). 평소엔 납작한 회색 알약. 손가락이
  // 닿아 있는 동안 투명한 렌즈로 부풀고, 다른 탭으로 이동하는 동안엔 물방울처럼
  // 옆으로 늘어난다 — 늘어남은 미끄러짐과 같은 시간이라 도착과 동시에 끝난다.
  //
  // pressSlot 은 라우트가 바뀌기 전에 알약을 손가락 밑으로 먼저 옮긴다. 라우트가
  // 실제로 바뀔 때만 지운다 — 손을 뗀 뒤 타이머로 지우면 이동이 늦을 때 알약이
  // 옛 탭으로 돌아갔다가 다시 오는 게 보였다.
  const [pressSlot, setPressSlot] = useState(null);
  const [pressing, setPressing] = useState(false);
  const pressSafety = useRef(null);
  const prevActive = useRef(activeSlot);
  useEffect(() => {
    if (prevActive.current === activeSlot) return;
    prevActive.current = activeSlot;
    clearTimeout(pressSafety.current);
    setPressSlot(null);
  }, [activeSlot]);
  useEffect(() => () => clearTimeout(pressSafety.current), []);
  const press = (slot) => ({
    onPointerDown: () => { clearTimeout(pressSafety.current); setPressSlot(slot); setPressing(true); },
    onPointerUp: () => {
      setPressing(false);
      // 이동하지 않는 탭(이미 그 화면)에 알약이 남지 않게.
      pressSafety.current = setTimeout(() => setPressSlot(null), 1500);
    },
    onPointerCancel: () => { setPressing(false); setPressSlot(null); },
  });
  const pillSlot = pressSlot ?? activeSlot;
  // 가운데 칸은 좁아서 알약은 라벨 칸 단위로 움직이고, 가운데를 지나면 한 칸 건너뛴다.
  const pillCol = pillSlot > CENTER ? pillSlot - 1 : pillSlot;

  // 이동 한 번에 물방울 한 번. 같은 키프레임 두 이름을 번갈아 써서 이동 중에 다시
  // 움직여도 무시되지 않고 다시 시작하고, 끝은 타이머가 아니라 animationend.
  const [moveRun, setMoveRun] = useState(0);
  const [moving, setMoving] = useState(false);
  const prevPill = useRef(pillSlot);
  const moveSafety = useRef(null);
  useEffect(() => {
    const from = prevPill.current;
    prevPill.current = pillSlot;
    if (from < 0 || pillSlot < 0 || from === pillSlot) return;
    setMoveRun((n) => n + 1);
    setMoving(true);
    // reduced motion 이면 애니메이션이 안 돌아 animationend 가 안 온다 — 백스톱.
    clearTimeout(moveSafety.current);
    moveSafety.current = setTimeout(() => setMoving(false), 600);
  }, [pillSlot]);
  useEffect(() => () => clearTimeout(moveSafety.current), []);
  const lens = pressing || moving;

  if (isTabBarHidden(pathname)) return null;

  return (
    <nav
      className="floating-nav"
      aria-label={t('navMain')}
      style={{ '--nav-col': pillCol, '--nav-past-center': pillSlot > CENTER ? 1 : 0 }}
    >
      {pillSlot >= 0 && (
        <span
          className={`floating-nav-pill${lens ? ' lens' : ''}${moving ? ` moving run-${moveRun % 2}` : ''}`}
          onAnimationEnd={() => setMoving(false)}
          aria-hidden="true"
        />
      )}
      {TABS.map((tab, i) => {
        const active = tab.match(pathname);
        if (tab.center) {
          return (
            <button
              key={tab.key}
              type="button"
              className={`floating-nav-btn floating-nav-btn--center${active ? ' active' : ''}`}
              onClick={() => { logTabClick(tab.key, 'mobile'); navigate(tab.path); }}
              aria-current={active ? 'page' : undefined}
              aria-label={t(tab.labelKey)}
              data-onboarding-id={`tab-${tab.key}`}
            >
              <span className="floating-nav-fab">
                <i className="material-symbols-rounded">{tab.icon}</i>
                <i className="material-symbols-rounded floating-nav-fab-plus" aria-hidden="true">add</i>
              </span>
            </button>
          );
        }
        return (
          <button
            key={tab.key}
            type="button"
            {...press(i)}
            className={`floating-nav-btn${active ? ' active' : ''}${lens && pillSlot === i ? ' under-lens' : ''}`}
            onClick={() => { logTabClick(tab.key, 'mobile'); navigate(tab.path); }}
            aria-current={active ? 'page' : undefined}
            data-onboarding-id={`tab-${tab.key}`}
          >
            <i className="material-symbols-rounded">{tab.icon}</i>
            <span className="floating-nav-label">{t(tab.labelKey)}</span>
          </button>
        );
      })}
    </nav>
  );
}
